import React from 'react';
import { Hourglass } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { HoverTip } from '../teams/premium';
import ChartCard from './ChartCard';
import { exportRowsToCsv, fmtCurrency, fmtNumber } from './chartTheme';

// The conversion funnel (Nic 2026-10-25): each step means "got at least
// this far", so the bars only ever step down. Between steps, the share that
// moved on. A step some leads reached without a record of their own (an
// account made with no invite logged) carries an amber "+N" with the why;
// those are listed under the funnel too, so the raw logged numbers are one
// subtraction away. The steps after signing take weeks to months, so a
// recent range reads thin there by nature; they say so.

const SLOW = new Set(['fotc', 'submitted', 'invoiced', 'paid']);

const BAR = {
  pre: 'from-sky-500 to-indigo-500',
  saSigned: 'from-emerald-500 to-teal-400',
  post: 'from-violet-400 to-violet-500',
};

// What a record gap means at each step, in plain words.
const GAP_WORDS = {
  contacted: (n) => `${n} got past this with no contact logged (they took a later step)`,
  invited: (n) => `${n} made an account with no invite logged`,
  account: (n) => `${n} went further with no account on record`,
  saSigned: (n) => `${n} went further with no signature on record`,
  fotc: (n) => `${n} were submitted or billed with no Ready for CM or In Process on record`,
  submitted: (n) => `${n} were billed with no VA submission on record`,
  invoiced: (n) => `${n} paid with no invoice on record`,
  paid: (n) => `${n} with no payment on record`,
};

const pctOf = (n, d) => (d ? Math.round((n / d) * 100) : null);

const FunnelSteps = ({ funnel = [], caption, filenameSuffix, loading }) => {
  const received = funnel[0]?.count || 0;
  const gaps = funnel.filter((s) => s.gap);

  const handleExport = () =>
    exportRowsToCsv(
      `leads-funnel_${filenameSuffix}`,
      funnel.map((s, i) => ({
        Stage: s.label,
        Count: s.count,
        'Of Received (%)': pctOf(s.count, received),
        'From Previous Step (%)': i ? pctOf(s.count, funnel[i - 1].count) : null,
        'Counted Without Its Own Record': s.gap || 0,
        ...(s.amount != null ? { Amount: s.amount } : {}),
      })),
    );

  return (
    <ChartCard eyebrow="Conversion funnel" title="How far these leads got" caption={caption} onExport={handleExport}>
      {loading ? (
        <div className="space-y-2.5">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-8 animate-pulse rounded-lg bg-slate-100 dark:bg-white/5" />
          ))}
        </div>
      ) : (
        <>
          <ol className="mb-0 list-none space-y-1 pl-0">
            {funnel.map((s, i) => {
              const prev = i ? funnel[i - 1] : null;
              const share = pctOf(s.count, received);
              const moved = prev ? pctOf(s.count, prev.count) : null;
              const slow = SLOW.has(s.stage);
              const tone = s.stage === 'saSigned' ? BAR.saSigned : slow ? BAR.post : BAR.pre;
              return (
                <li key={s.stage}>
                  {prev ? (
                    <div className="grid grid-cols-[7.5rem_1fr] gap-3 sm:grid-cols-[9rem_1fr_8.5rem]">
                      <span />
                      <span className="pl-1 text-[10px] font-semibold tabular-nums text-slate-400">
                        {moved == null ? '' : `↓ ${moved}% moved on`}
                      </span>
                    </div>
                  ) : null}
                  <div className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1 sm:grid-cols-[9rem_1fr_8.5rem]">
                    <span
                      className={cn(
                        'flex items-center gap-1.5 text-sm font-medium',
                        slow ? 'text-slate-500 dark:text-slate-400' : 'text-slate-800 dark:text-white',
                      )}
                    >
                      {slow ? <Hourglass size={12} className="shrink-0 text-slate-300" aria-label="Takes weeks to months" /> : null}
                      {s.label}
                    </span>
                    <div className="relative h-8 overflow-hidden rounded-lg bg-slate-100 dark:bg-white/5">
                      <div
                        className={cn(
                          'h-full rounded-lg bg-gradient-to-r transition-[width] duration-700',
                          tone,
                          slow && 'opacity-80',
                        )}
                        style={{ width: `${received ? Math.max(s.count ? 1.5 : 0, (s.count / received) * 100) : 0}%` }}
                      />
                    </div>
                    <div className="col-span-2 flex items-baseline justify-end gap-2 sm:col-span-1">
                      {s.gap ? (
                        <HoverTip label={GAP_WORDS[s.stage]?.(s.gap) || `${s.gap} with no record of this step`} wide align="right">
                          <span className="rounded-full bg-amber-50 px-1.5 py-px text-[10px] font-semibold tabular-nums text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                            +{s.gap}
                          </span>
                        </HoverTip>
                      ) : null}
                      <span className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                        {fmtNumber(s.count)}
                      </span>
                      <span className="w-10 text-right text-xs tabular-nums text-slate-400">
                        {share == null ? '-' : `${share}%`}
                      </span>
                    </div>
                  </div>
                  {s.amount != null ? (
                    <div className="grid grid-cols-[7.5rem_1fr] gap-3 sm:grid-cols-[9rem_1fr_8.5rem]">
                      <span />
                      <span className="hidden sm:block" />
                      <span className="pr-12 text-right text-[11px] font-semibold tabular-nums text-emerald-600">
                        {fmtCurrency(s.amount)}
                      </span>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>

          <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
            {gaps.length ? (
              <div className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900 dark:bg-amber-500/10 dark:text-amber-100">
                <div className="mb-1 font-semibold">Steps with no record of their own</div>
                <ul className="mb-1 list-none space-y-0.5 pl-0">
                  {gaps.map((s) => (
                    <li key={s.stage}>
                      <b className="tabular-nums">{s.label}:</b> {GAP_WORDS[s.stage]?.(s.gap)}
                    </li>
                  ))}
                </ul>
                <div className="text-amber-800/80 dark:text-amber-200/70">
                  They still count at the step, since they got past it. The lead export lists each one.
                </div>
              </div>
            ) : null}
            <div className="rounded-xl bg-slate-50 px-3.5 py-2.5 text-xs text-slate-500 dark:bg-white/5 dark:text-slate-400">
              <div className="mb-1 flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-200">
                <Hourglass size={12} /> FOTC through Paid take weeks to months
              </div>
              A recent range always looks thin there; those leads are still moving.
            </div>
          </div>
        </>
      )}
    </ChartCard>
  );
};

export default FunnelSteps;
